import ObexRequestsHandlerP80 from '../handlers/ObexRequestsHandlerP80';
import {
  GoodsReceipt,
  GoodsReceiptLine,
  GoodsReceiptLineResult,
  GoodsReceiptPage,
  GoodsReceiptPayload,
  GoodsReceiptLinePayload
} from '../types/GoodsReceipt.types';
import { PurchaseOrderStatus } from '../types/PurchaseOrder.types';
import { getAccessToken, buildQuery, unwrapPage, unwrapRecord, unwrapError, getTransportError } from './apiUtils';

// Solo hay alta y consulta: un albarán no se edita ni se borra. El trigger de
// goods_receipt_lines acumula lo recibido en la línea de pedido y recalcula
// el estado del albarán (partial/complete) y de la OC.
const ROUTES = {
  list:    '/goods_receipts_list',
  get:     '/goods_receipts_get',
  create:  '/goods_receipts_create',
  lineAdd: '/goods_receipt_lines_add'
};

const SESSION_ERROR = 'Sesión no válida o expirada.';

const toId = (value: any): string | null =>
  value === undefined || value === null ? null : String(value);

const toNumber = (value: any, fallback = 0): number => {
  const n = Number(value);
  return isNaN(n) ? fallback : n;
};

export const mapGoodsReceiptLine = (raw: any): GoodsReceiptLine => ({
  id:                   String(raw.id),
  goodsReceiptId:       toId(raw.goods_receipt_id) || '',
  purchaseOrderLineId:  toId(raw.purchase_order_line_id) || '',
  quantityReceived:     toNumber(raw.quantity_received),
  description:          raw.description || '',
  sku:                  raw.sku || '',
  bookId:               toId(raw.book_id),
  quantityOrdered:      toNumber(raw.quantity_ordered),
  lineQuantityReceived: toNumber(raw.line_quantity_received)
});

export const mapGoodsReceipt = (raw: any): GoodsReceipt => ({
  id:                 String(raw.id),
  purchaseOrderId:    toId(raw.purchase_order_id) || '',
  poNumber:           raw.po_number || '',
  warehouseId:        toId(raw.warehouse_id) || '',
  receivedBy:         toId(raw.received_by),
  receivedAt:         raw.received_at || '',
  deliveryNoteNumber: raw.delivery_note_number || '',
  packageCount:       raw.package_count === null || raw.package_count === undefined ? null : toNumber(raw.package_count),
  status:             raw.status === 'complete' ? 'complete' : 'partial',
  notes:              raw.notes || '',
  lines:              Array.isArray(raw.lines) ? raw.lines.map(mapGoodsReceiptLine) : []
});

export const listGoodsReceipts = async (
  options: { purchaseOrderId?: string; warehouseId?: string; page?: number; pageSize?: number } = {}
): Promise<GoodsReceiptPage> => {
  const token = getAccessToken();
  if (!token) throw new Error(SESSION_ERROR);

  const query = buildQuery({
    access_token:      token,
    purchase_order_id: options.purchaseOrderId,
    warehouse_id:      options.warehouseId,
    page:              options.page || 1,
    page_size:         options.pageSize || 50
  });

  const res = await ObexRequestsHandlerP80.get(`${ROUTES.list}${query}`);
  const transportError = getTransportError(res);
  if (transportError) throw new Error(`No se pudo cargar el listado de recepciones: ${transportError}`);
  const page = unwrapPage(res);
  return {
    data:     page.rows.map(mapGoodsReceipt),
    total:    page.total,
    page:     page.page,
    pageSize: page.pageSize
  };
};

/** Cabecera + líneas del albarán (con descripción y cantidades de la línea de pedido). */
export const getGoodsReceipt = async (id: string): Promise<GoodsReceipt> => {
  const token = getAccessToken();
  if (!token) throw new Error(SESSION_ERROR);

  const res = await ObexRequestsHandlerP80.get(`${ROUTES.get}${buildQuery({ access_token: token, id })}`);
  const record = unwrapRecord(res);
  if (!record) throw new Error(unwrapError(res, 'Albarán no encontrado.'));
  return mapGoodsReceipt(record);
};

// La OC debe estar approved o partially_received y el almacén ser físico;
// el backend lo valida y devuelve el motivo como texto.
export const createGoodsReceipt = async (payload: GoodsReceiptPayload): Promise<GoodsReceipt> => {
  const token = getAccessToken();
  if (!token) throw new Error(SESSION_ERROR);

  const query = buildQuery({
    access_token:         token,
    purchase_order_id:    payload.purchaseOrderId,
    warehouse_id:         payload.warehouseId,
    delivery_note_number: payload.deliveryNoteNumber,
    package_count:        payload.packageCount,
    notes:                payload.notes
  });

  const res = await ObexRequestsHandlerP80.get(`${ROUTES.create}${query}`);
  const record = unwrapRecord(res);
  if (!record) throw new Error(unwrapError(res, 'Error al registrar el albarán.'));
  return mapGoodsReceipt(record);
};

// El backend rechaza cantidades <= 0 y las que superen lo pendiente de la línea.
export const addGoodsReceiptLine = async (payload: GoodsReceiptLinePayload): Promise<GoodsReceiptLineResult> => {
  const token = getAccessToken();
  if (!token) throw new Error(SESSION_ERROR);

  const query = buildQuery({
    access_token:           token,
    goods_receipt_id:       payload.goodsReceiptId,
    purchase_order_line_id: payload.purchaseOrderLineId,
    quantity_received:      payload.quantityReceived
  });

  const res = await ObexRequestsHandlerP80.get(`${ROUTES.lineAdd}${query}`);
  const record = unwrapRecord(res);
  if (!record) throw new Error(unwrapError(res, 'Error al registrar la línea del albarán.'));

  return {
    line: mapGoodsReceiptLine(record),
    purchaseOrderStatus: (record.purchase_order && record.purchase_order.status) as PurchaseOrderStatus
  };
};

export interface FailedReceiptLine {
  purchaseOrderLineId: string;
  error: string;
}

/**
 * Recepción completa: albarán y después sus líneas, una a una. Sin transacción:
 * si una línea falla, el albarán queda con las que entraron y se devuelven los
 * fallos. purchaseOrderStatus es el estado de la OC tras la última línea que
 * entró (partially_received o received), o null si no entró ninguna.
 */
export const createGoodsReceiptWithLines = async (
  header: GoodsReceiptPayload,
  lines: { purchaseOrderLineId: string; quantityReceived: number }[]
): Promise<{ receipt: GoodsReceipt; purchaseOrderStatus: PurchaseOrderStatus | null; failedLines: FailedReceiptLine[] }> => {
  const receipt = await createGoodsReceipt(header);
  const failedLines: FailedReceiptLine[] = [];
  let purchaseOrderStatus: PurchaseOrderStatus | null = null;

  for (const line of lines) {
    if (line.quantityReceived <= 0) continue;
    try {
      const result = await addGoodsReceiptLine({
        goodsReceiptId:      receipt.id,
        purchaseOrderLineId: line.purchaseOrderLineId,
        quantityReceived:    line.quantityReceived
      });
      purchaseOrderStatus = result.purchaseOrderStatus || purchaseOrderStatus;
    } catch (err) {
      failedLines.push({
        purchaseOrderLineId: line.purchaseOrderLineId,
        error: err instanceof Error ? err.message : 'Error desconocido'
      });
    }
  }

  return { receipt, purchaseOrderStatus, failedLines };
};
